import {Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {SamplesTableComponent} from "./samples-table/samples-table.component";
import {MatTableDataSource} from "@angular/material/table";
import {MatDialog} from "@angular/material/dialog";
import {AddSamplesDialogComponent} from "../../dialogs/lab-manager/add-samples-dialog/add-samples-dialog.component";
import {data} from "autoprefixer";
import {
  DraftConfirmationDialogComponent
} from "../../dialogs/draft-confirmation-dialog/draft-confirmation-dialog.component";
import {LabManagerService} from "../lab-manager.service";
import {LabSampleModel} from "../../models/lab-manager/lab-sample";
import {NavBarComponent} from "../../nav-bar/nav-bar.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import dayjs from "dayjs";
import {PageEvent} from "@angular/material/paginator";
import {
  AnalysisRequiredDialogComponent
} from "../../dialogs/lab-manager/analysis-required-dialog/analysis-required-dialog.component";
import {LabSubmissionDetailsModel} from "../../models/lab-manager/lab-submission-details";
import {CustomerModel} from "../../order-to-cash/models-otc/customer-model";
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from "@angular/material/core";
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from "@angular/material-moment-adapter";
import {MergePanelGroup, labMergePanelGroup} from "../../shared/merge-panel/merge-panel.model";

export const LAB_MANAGER_DATE_FORMAT = {
  parse: {
    dateInput: "YYYY-MM-DD"
  },
  display: {
    dateInput: "YYYY-MM-DD",
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY"
  }
};

@Component({
    selector: 'app-lab-samples',
    templateUrl: './lab-samples.component.html',
    styleUrls: ['./lab-samples.component.css'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: LAB_MANAGER_DATE_FORMAT }
    ],
    standalone: false
})
export class LabSamplesComponent implements OnInit, OnChanges {

  /** The table owns the tick boxes, so clearing them has to go through it. See finishRemoval. */
  @ViewChild(SamplesTableComponent) samplesTable: SamplesTableComponent;

  @Input() selectedCustomer: CustomerModel;
  @Input() submissionId: number = 0;
  @Input() pinTypeId: number = 0;
  @Input() submissionStatus: number = null;
  currentPage: number = 0;
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  displayedColumns: string[] = [
    'checkbox',
    'sample',
    'sampleType',
    'dateCreated',
    'farm',
    'field',
    'hectare',
    'createdBy',
    'linkedGroup',
    'action'
  ];
  filterStartDate: string = '';
  filterEndDate: string = '';
  pageSize: number = 9999;
  searchText: string = '';
  startDate: string = '';
  endDate: string = '';
  tableHeight: string = (window.innerHeight - 500) + 'px';
  totalElements: number = 0;
  timerInterval: any = null;
  selectedSamples: number[] = [];
  isFilterBtnVisible: boolean = false;
  mergeGroups: any[] = [];
  @Output() sampleChange: EventEmitter<any> = new EventEmitter<any>();

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.tableHeight = (window.innerHeight - 500) + 'px';
  }

  constructor(
      private dialog: MatDialog,
      private labManagerService: LabManagerService,
      private nav: NavBarComponent,
      private snackBar: MatSnackBar
  ) {
  }

  ngOnInit(): void {
  }

  resetFilter(){
    this.searchText = '';
    this.startDate = '';
    this.endDate = '';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.isFilterBtnVisible = false;
    this.getLabSampleListById();
  }

  getLabSampleListById() {
    if(this.submissionId) {
      this.nav.toggleLoading(true);
      if(this.timerInterval) {
        clearInterval(this.timerInterval);
      }
      this.sampleChange.emit();
      this.labManagerService.getLabSamplesListById(this.submissionId, this.currentPage, this.pageSize, this.startDate, this.endDate, this.searchText).subscribe({
        next: (res: any) => {
          this.totalElements = res.totalElements;
          this.dataSource = res;
          this.nav.toggleLoading(false);
        },
        error: () => {
          this.nav.toggleLoading(false);
        }
      })
    }
  }

  openAddSamplesDialog() {
    const dialogRef = this.dialog.open(AddSamplesDialogComponent, {
      data: {
        tenantId: this.selectedCustomer.tenant.id,
        pinTypeId: this.pinTypeId
      },
      width: '1590px',
      height: '825px'
    });

    dialogRef.afterClosed().subscribe((sampleList: LabSampleModel[]) => {
      if(sampleList?.length) {
        // ONE call, not one per sample. This was a loop firing a POST per sample with no
        // sequencing, so every request read the submission's highest position independently and
        // they all took it: several samples ended up sharing a position and the order came out
        // arbitrary. The API places the whole batch in one transaction instead, each sample
        // beside its nearest neighbour by bag number.
        //
        // It is also atomic now. The old loop counted responses and refreshed on the last one
        // whether it succeeded or failed, so a partial add looked like a complete one.
        this.nav.toggleLoading(true);
        this.labManagerService.addLabSamples(Number(this.submissionId), sampleList.map(s => s.id)).subscribe({
          next: () => {
            this.nav.toggleLoading(false);
            this.getLabSampleListById();
          },
          error: () => {
            this.nav.toggleLoading(false);
            this.snackBar.open('Could not add the samples', 'Dismiss', { duration: 5000 });
            this.getLabSampleListById();
          }
        });
      }
    })
  }

  filterDateRange() {
    if(this.filterStartDate && this.filterEndDate) {
      this.isFilterBtnVisible = true;
      this.startDate = this.filterStartDate ? dayjs(this.filterStartDate).format('YYYY-MM-DD') : '';
      this.endDate = this.filterEndDate ? dayjs(this.filterEndDate).format('YYYY-MM-DD') : '';
      this.getLabSampleListById();
    }
  }

  removeSamplesDialog(selectedSamples: any[]) {
    const dialogRef = this.dialog.open(AnalysisRequiredDialogComponent, {
      data: {
        dialogTitle: 'REMOVE SAMPLES',
        dialogType: 'remove',
        selectedLength: selectedSamples.length,
        totalSamples: this.totalElements
      },
      width: '420px'
    });

    dialogRef.afterClosed().subscribe({
      next: (res: any) => {
        if(res === 1) {
          this.removeSamples(selectedSamples);
        } else if(res === 2) {
          this.nav.toggleLoading(true);
          this.labManagerService.removeAllSamples(this.submissionId).subscribe({
            next: () => {
              this.nav.toggleLoading(false);
              this.getLabSampleListById();
            },
            error: () => {
              this.nav.toggleLoading(false);
              this.getLabSampleListById();
            }
          })
        }
      }
    })
  }

  searchLabSamples() {
    if(this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      this.isFilterBtnVisible = this.searchText.length > 0;
      this.getLabSampleListById();
    }, 800);
  }

  // === Sample order ===

  /** The full ordered list of sample ids, held until the user saves. */
  pendingOrder: number[] | null = null;
  savingOrder = false;

  get orderDirty(): boolean {
    return this.pendingOrder !== null;
  }

  onOrderChanged(orderedSampleIds: number[]): void {
    this.pendingOrder = orderedSampleIds;
  }

  /**
   * Saves the order for the whole submission.
   *
   * The positions the API stores are derived from the array index, so the first
   * save stamps every row rather than only the one that moved. That is deliberate:
   * it keeps the submission either fully ordered or fully unordered, so the list
   * can never end up half numbered with the rest falling back to the default sort.
   */
  saveOrder(): void {
    if (!this.pendingOrder || !this.submissionId) return;
    this.savingOrder = true;
    this.nav.toggleLoading(true);
    this.labManagerService.saveSampleOrder(this.submissionId, this.pendingOrder).subscribe({
      next: () => {
        this.pendingOrder = null;
        this.savingOrder = false;
        this.getLabSampleListById();
      },
      error: () => {
        // The reorder is still on screen, so say so rather than leaving the user
        // to assume it saved.
        this.savingOrder = false;
        this.nav.toggleLoading(false);
        this.snackBar.open('Could not save the sample order. Your changes are still here.', 'Dismiss', { duration: 6000 });
      }
    });
  }

  /**
   * Replaces the current order with the default: bag number first, then farm, field
   * and sample name.
   *
   * The API stamps those positions rather than clearing the column, so the result
   * reaches Recommendation Manager instead of leaving it to fall back to the row
   * order of the lab's own file.
   *
   * Confirmed first. It discards an arrangement somebody may have spent real time
   * on, there is no undo, and the button sits directly beside Save Order where a
   * misclick costs the lot.
   */
  resetOrder(): void {
    if (!this.submissionId) return;

    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'remove',
        dialogTitle: 'RESET SAMPLE ORDER',
        dialogSubTitle: 'This replaces the current order with the default, by bag number. Any order you arranged will be lost. Continue?'
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if (!res) return;
      this.applyResetOrder();
    });
  }

  private applyResetOrder(): void {
    this.savingOrder = true;
    this.nav.toggleLoading(true);
    this.labManagerService.resetSampleOrder(this.submissionId).subscribe({
      next: () => {
        this.pendingOrder = null;
        this.savingOrder = false;
        this.getLabSampleListById();
      },
      error: () => {
        this.savingOrder = false;
        this.nav.toggleLoading(false);
        this.snackBar.open('Could not reset the sample order.', 'Dismiss', { duration: 6000 });
      }
    });
  }

  removeSamples(selectedSamples: any[]) {
    let count = 0;
    this.nav.toggleLoading(true);
    for(let sample of selectedSamples) {
      this.labManagerService.removeLabSample(sample.id).subscribe({
        next: () => {
          count++;
          if(count === selectedSamples.length) {
            this.finishRemoval();
          }
        },
        error: () => {
          count++;
          if(count === selectedSamples.length) {
            // Refreshed on failure too. Some of the deletes may have gone through, and leaving
            // the old list up showed samples that were already gone.
            this.finishRemoval();
            this.snackBar.open('Some samples could not be removed.', 'Dismiss', { duration: 6000 });
          }
        }
      })
    }
  }

  /**
   * After a removal: stop the loader, drop the tick boxes, reload the list.
   *
   * The selection is cleared because the rows it points at are gone. It lives inside the table
   * component, which owns its own array, so it has to be told rather than reset from here. Until
   * this was added the count and the Remove and Merge buttons stayed on screen after a removal,
   * referring to samples that no longer existed. Reported 28/10/2026.
   */
  private finishRemoval(): void {
    this.nav.toggleLoading(false);
    this.samplesTable?.clearSelection();
    this.getLabSampleListById();
  }

  removeSampleDialog(selectedSample: any) {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'remove',
        dialogTitle: 'REMOVE SAMPLE',
        dialogSubTitle: 'Are you sure you want to remove the sample?'
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if(res) {
        this.removeSamples([selectedSample]);
      }

    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes?.submissionId) {
      this.getLabSampleListById();
      this.loadMergeGroups();
    }
  }

  loadMergeGroups() {
    if (this.submissionId) {
      this.labManagerService.getMergeGroups(this.submissionId).subscribe({
        next: (groups) => this.mergeGroups = groups || [],
        error: () => this.mergeGroups = []
      });
    }
  }

  mergePanelOpen = false;
  mergePanelGroup: MergePanelGroup | null = null;

  /**
   * The row the panel was opened from, held so the panel can be rebuilt after a
   * removal. The group payload names its members but not the primary field and
   * farm, which only the row carries.
   */
  mergePanelPrimary: any = null;

  /**
   * Shows what a merge group contains.
   *
   * The primary row is passed through from the table because the group payload
   * names its linked samples but not the primary field and farm, and the
   * primary is a row the table already holds.
   */
  viewMergeGroup(event: { group: any; element: any }): void {
    const group = labMergePanelGroup(event?.group, {
      sampleId: event?.element?.id ?? null,
      fieldName: event?.element?.pinFieldName ?? event?.element?.pinSamplesPinFieldName ?? null,
      farmName: event?.element?.pinFieldFarmName ?? event?.element?.pinSamplesPinFieldFarmName ?? null,
    });
    if (!group) {
      return;
    }
    this.mergePanelPrimary = event?.element ?? null;
    this.mergePanelGroup = group;
    this.mergePanelOpen = true;
  }

  closeMergePanel(): void {
    this.mergePanelOpen = false;
    this.mergePanelGroup = null;
    this.mergePanelPrimary = null;
  }

  /**
   * Whether the panel may offer to take a sample out of a group.
   *
   * The same boundary the rest of this screen uses for editability. Status 2 is
   * the point the sheet goes to the lab, after which the results have been
   * copied onto every member of a group and freeing one would strand them.
   */
  get canUnmerge(): boolean {
    return this.submissionStatus < 2;
  }

  /**
   * Takes one sample out of the group the panel is showing.
   *
   * Reloads the list and the groups, then rebuilds the panel from the reloaded
   * groups so it shows what is actually there. Closes it when the group is gone,
   * which happens when its last member leaves.
   */
  unmergeMember(member: { sampleId: number | null }): void {
    if (member?.sampleId == null) {
      return;
    }
    this.nav.toggleLoading(true);
    this.labManagerService.unlinkSample(member.sampleId).subscribe({
      next: (res) => {
        this.nav.toggleLoading(false);
        this.getLabSampleListById();
        if (!res?.groupRemains) {
          this.closeMergePanel();
          this.loadMergeGroups();
          return;
        }
        // Rebuilt from the server rather than spliced out locally, so what the
        // panel shows cannot drift from what the submission actually holds.
        this.labManagerService.getMergeGroups(this.submissionId).subscribe({
          next: (groups) => {
            this.mergeGroups = groups || [];
            this.refreshMergePanel();
          },
          error: () => this.closeMergePanel()
        });
      },
      error: () => {
        this.nav.toggleLoading(false);
        this.loadMergeGroups();
        this.closeMergePanel();
      }
    });
  }

  /** Re-reads the open panel from the current groups, closing it if it has gone. */
  private refreshMergePanel(): void {
    const primary = this.mergePanelPrimary;
    const group = this.mergeGroups.find(g => g.primarySampleId === primary?.id);
    if (!group) {
      this.closeMergePanel();
      return;
    }
    this.mergePanelGroup = labMergePanelGroup(group, {
      sampleId: primary?.id ?? null,
      fieldName: primary?.pinFieldName ?? primary?.pinSamplesPinFieldName ?? null,
      farmName: primary?.pinFieldFarmName ?? primary?.pinSamplesPinFieldFarmName ?? null,
    });
    if (!this.mergePanelGroup) {
      this.closeMergePanel();
    }
  }

  getMergeCount(sampleId: number): number {
    const group = this.mergeGroups.find(g => g.primarySampleId === sampleId);
    return group ? group.linkedCount + 1 : 0;
  }

  mergeSamples(selectedSamples: LabSampleModel[]) {
    if (selectedSamples.length < 2) return;
    const primaryId = selectedSamples[0].id;
    const linkedIds = selectedSamples.slice(1).map(s => s.id);

    this.nav.toggleLoading(true);
    this.labManagerService.mergeSamples(this.submissionId, primaryId, linkedIds).subscribe({
      next: () => {
        this.nav.toggleLoading(false);
        this.getLabSampleListById();
        this.loadMergeGroups();
      },
      error: (err) => {
        this.nav.toggleLoading(false);
        // The API refuses a merge whose samples differ in depth or rock percentage, and its
        // reason names the samples and the values. This handler used to discard it, so a
        // refused merge looked exactly like a button that did nothing.
        this.snackBar.open(
          err?.error?.message || 'Could not merge the samples.',
          'Dismiss',
          { duration: 8000 }
        );
      }
    });
  }

  unmergeSamples(primarySampleId: number) {
    this.nav.toggleLoading(true);
    this.labManagerService.unmergeSamples(primarySampleId).subscribe({
      next: () => {
        this.nav.toggleLoading(false);
        this.getLabSampleListById();
        this.loadMergeGroups();

        // The group no longer exists, so a panel describing it has to close. Only when it is
        // THIS group: unmerging one while looking at another should leave that one alone.
        if (this.mergePanelPrimary?.id === primarySampleId) {
          this.closeMergePanel();
        }
      },
      error: () => {
        this.nav.toggleLoading(false);
      }
    });
  }
}
